import React, { useContext, useEffect, useMemo } from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { useLoaderData } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { sendScreenEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { HelmetContext } from 'src/components/Helmet';
import { useReferrer } from 'src/hooks/useReferrer';
import { ROVO_HUB_FF } from 'src/constants';
import { isDevelopment } from 'src/services/http/user';

import { RovoHubLoaderProps } from './loader';
import ContentSection, { SupportedContentSectionTypes } from './children/ContentSection';
import PageHeading from './children/PageHeading';

export const RovoHubPage: React.FC = () => {
  const { user, isLoading, hasError } = useAtlassianUser();
  const helmet = useContext(HelmetContext);
  const { referrerPath, isInitialized } = useReferrer();

  const { rovoHubData } = useLoaderData() as RovoHubLoaderProps;

  const isRovoHubEnabled = FeatureGates.checkGate(ROVO_HUB_FF);

  helmet.setHelmet({
    title: `${rovoHubData.titleExternal} | Learning - Atlassian Community`,
    seoTags: rovoHubData.seo,
  });

  useEffect(() => {
    if (!isLoading && !hasError && isInitialized) {
      sendScreenEvent('rovoHubScreen', { rovoHubReferrerPath: referrerPath }, user?.account_id);
    }
  }, [user, isLoading, hasError, referrerPath, isInitialized]);

  const contentSections = useMemo(
    () =>
      rovoHubData.contentContainerCollection.items.map((contentBlock) => (
        <ContentSection key={(contentBlock as SupportedContentSectionTypes).sys.id} contentBlock={contentBlock} />
      )),
    [rovoHubData.contentContainerCollection.items],
  );

  // TODO: remove this once Rovo Hub is enabled
  if (!isDevelopment() && !isRovoHubEnabled) {
    window.location.href = '/learning';

    return null;
  }

  return (
    <main data-testid="rovo-hub" css={pageContainerStyles}>
      <PageHeading title={rovoHubData.hero.titleExternal} subtitle={rovoHubData.hero.subtitle} />

      {/* content */}
      <div data-testid="rovo-hub-content-section" css={outerContentContainerStyles}>
        <div css={innerContentContainerStyles}>{contentSections}</div>
      </div>
    </main>
  );
};

const pageContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  marginTop: '136px',
  gap: token('space.1000'),

  '@media (max-width: 1279px)': {
    marginTop: '0',
  },

  '& [data-testid="hero"]': {
    margin: '0 auto',
  },

  [media.below.md]: {
    gap: token('space.600'),
  },
});

const outerContentContainerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  [media.below.md]: {
    padding: `0 ${token('space.200')}`,
  },
});

const innerContentContainerStyles = css({
  maxWidth: '1024px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  rowGap: `calc(${token('space.1000')} * 2)`,
  alignSelf: 'center',

  [media.below.md]: {
    rowGap: `calc(${token('space.600')} * 2)`,
  },
});
